import { useState, useRef, useEffect, useCallback } from "react";

type UseCountdownOptions = {
  seconds: number;
  onExpire?: () => void;
};

export function useCountdown({ seconds, onExpire }: UseCountdownOptions) {
  const [count, setCount] = useState(seconds);
  const intervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clear();
    setCount(seconds);
    startTimeRef.current = null;
  }, [clear, seconds]);

  const start = useCallback(() => {
    clear();
    startTimeRef.current = Date.now();

    intervalRef.current = window.setInterval(() => {
      if (startTimeRef.current === null) return;

      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const remaining = Math.max(seconds - elapsed, 0);

      setCount(remaining);

      if (remaining === 0) {
        clear();
        if (onExpire) onExpire();
      }
    }, 250); // check frequently for smoother & accurate countdown
  }, [clear, seconds, onExpire]);

  useEffect(() => {
    setCount(seconds);
    return clear;
  }, [seconds]);

  return { count, start, reset, clear };
}
